import { Box } from "@mui/system";
import ActionItems from "../ActionItems/ActionItems";
import ActionItemService from "../../services/actionItems/ActionItemService";
import {
  ActionItemsDashboardResponse,
  ActionItemType,
  ChartDataItem,
  ColumnVisibility,
  DateRangeValue,
  Options,
  SelectedChartBar,
  UserTeamData,
} from "../../utils/types";
import {
  ACTION_ITEM_GROUP_BY_OPTIONS,
  ActionItemGroupByKey,
  capitalizeFirstLetter,
  DELAY_CATEGORY_CONFIG,
  formatDateOnly,
  getDisplayName,
  getLastTwoYearsToYesterdayRange,
  getStatusOptions,
  getUniqueAccounts,
  getUniqueAssignees,
  isValidDate,
  PRIORITY_BAR_CONFIG,
  PRIORITY_OPTION,
} from "../../utils/utils";
import { useContext, useEffect, useMemo, useState } from "react";
import { initialSort } from "../ActionItems/actionItemTableConfig";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import StatusBreakdownCard from "./AnalyticsTasksStatus";
import UserInteractionsService from "../../services/interactions/Interactions";
import ColumnFilter from "../Common/customFilter";
import AnalyticsBarCard from "./AnalyticsBarCard";
import FilterPill from "../Common/FilterPill";
import ClientService from "../../services/clients/ClientService";
import { getUserAttributes } from "../../utils/auth";
import { GlobalContext } from "../../context/GlobalContext";

interface AnalyticsTasksProps {
  selectedTab: "my-analytics" | "team-analytics" | "account-analytics";
  analyticsDateRange: DateRangeValue;
  newUserTeamData: UserTeamData | null;
  setAnalyticsDateRange: React.Dispatch<React.SetStateAction<DateRangeValue>>
}
const AnalyticsTasks = ({
  selectedTab,
  analyticsDateRange,
  newUserTeamData,
  setAnalyticsDateRange,
}: AnalyticsTasksProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [actionItems, setActionItems] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [sort, setSort] = useState<{
    sort_field: string;
    sort_order: "asc" | "desc";
  }>(initialSort);
  const { accountDetails } = useContext(GlobalContext);
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [spacesList, setSpacesList] = useState<any[]>([]);
  const [clientList, setClientList] = useState<any[]>([]);
  const [actionItemType, setActionItemType] = useState(ActionItemType.All);
  const { showSnackbar } = useDisplaySnackbar();
  const UserInfo = getUserAttributes();
  const loggedInEmail = UserInfo?.email;
  const isSeventhGearUser = loggedInEmail?.endsWith("@7thgear.ai");
  const [statusType, setStatusType] = useState<string>("due");
  const allowedStatusKeys = useMemo(() => {
    if (statusType === "overdue") {
      return isSeventhGearUser
        ? ["to_do", "in_progress", "pr_review", "product_testing"]
        : ["to_do", "in_progress"];
    }
  
    return isSeventhGearUser
      ? ["to_do", "in_progress", "done", "pr_review", "product_testing"]
      : ["to_do", "in_progress", "done"];
  }, [statusType, isSeventhGearUser]);
  
  const statusFilterString = useMemo(
    () => allowedStatusKeys.join(","),
    [allowedStatusKeys]
  );  
  const [groupByList, setGroupByList] = useState<
    { status: string; totalCount: number }[]
  >([]);
  const filteredGroupByList = useMemo(() => {
    return groupByList.filter((item) =>
      allowedStatusKeys.includes(item.status)
    );
  }, [groupByList, allowedStatusKeys]);  
  const statusOptions = getStatusOptions();
  const filteredStatusOptions = useMemo(() => {
    return isSeventhGearUser
      ? statusOptions
      : statusOptions.filter((opt) =>
          ["to_do", "in_progress", "done", "wont_do"].includes(opt.value)
        );
  }, [isSeventhGearUser, statusOptions]);
  
  const totalTaskCount = useMemo(() => {
    return filteredGroupByList.reduce(
      (sum, item) => sum + (item?.totalCount ?? 0),
      0
    );
  }, [filteredGroupByList]);
  const [groupBy, setGroupBy] = useState<ActionItemGroupByKey>(
    ActionItemGroupByKey.PRIORITY
  );
  const [dashBoardList, setDashBoardList] =
    useState<ActionItemsDashboardResponse | null>(null);
  const [selectedChartBar, setSelectedChartBar] =
    useState<SelectedChartBar | null>(null);
  const chartBarPills = useMemo(() => {
    if (!selectedChartBar) return [];

    switch (selectedChartBar.groupBy) {
      case ActionItemGroupByKey.CLIENT:
        return [{ key: "account", value: selectedChartBar.key }];

      case ActionItemGroupByKey.PRIORITY:
        return [{ key: "priority", value: selectedChartBar.key }];

      case ActionItemGroupByKey.RESOURCES:
        return [{ key: "assignee", value: selectedChartBar.key }];

      case ActionItemGroupByKey.DELAY:
        return [{ key: "delay", value: selectedChartBar.key }];

      default:
        return [];
    }
  }, [selectedChartBar]);

  const chartBarApiFilters = useMemo(() => {
    return chartBarPills.reduce(
      (acc, { key, value }) => {
        if (key === "account") acc.clientOrgId = value;
        if (key === "priority") acc.priority = value;
        if (key === "assignee") acc.assigneePorIds = [value];
        if (key === "delay") acc.delayCategory = value;
        return acc;
      },
      {
        clientOrgId: "",
        priority: "",
        delayCategory: "",
        assigneePorIds: [] as string[],
      }
    );
  }, [chartBarPills]);

  const [selectedStatus, setSelectedStatus] = useState<{
    key: string;
    label: string;
    count: number;
  }>({
    key: "total",
    label: "Total",
    count: totalTaskCount,
  });
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({
    serialnumber: false,
    description: true,
    account: true,
    assignee: true,
    coach: false,
    dueDate: true,
    priority: true,
    status: true,
    columnFilter: true,
    momlink: false,
    sponsor: false,
    tags: false,
    subscribers: false,
    createdAt: false,
    actions: false,
  });

  const employeePorId = selectedTab === "team-analytics" ? newUserTeamData?.currentUser : loggedInProfileOrgId;
  const shouldCallApi =
    !!employeePorId &&
    isValidDate(analyticsDateRange?.startDate) &&
    isValidDate(analyticsDateRange?.endDate);

  const isForTeamAnalytics = selectedTab === "team-analytics";
  const isLoggedInUserSelectedInTeam = selectedTab === "team-analytics" && newUserTeamData?.currentUser === loggedInProfileOrgId;
  const isTeamResourcesView = selectedTab === "team-analytics" && groupBy === ActionItemGroupByKey.RESOURCES;
  const includeManagerTasksForPageApi = selectedTab === "team-analytics" && !isLoggedInUserSelectedInTeam;
  const includeManagerTasksForGroupByApi = selectedTab === "team-analytics" && !isLoggedInUserSelectedInTeam;
  const includeManagerTasksForDashboardApi = selectedTab === "team-analytics" && (!isLoggedInUserSelectedInTeam || isTeamResourcesView);

  const {
    isLoading: isActionItemCountLoading,
    isFetching: isActionItemCountFetching,
    isSuccess: isActionItemCountSuccess,
    isError: isActionItemCountError,
    error: actionItemCountError,
    data: actionItemCountData,
  } = ActionItemService.useGetActionItemCount(
    {
      employeePorId: chartBarApiFilters.assigneePorIds?.[0] ?? employeePorId ?? loggedInProfileOrgId,
      startTime: formatDateOnly(analyticsDateRange?.startDate),
      endTime: formatDateOnly(analyticsDateRange?.endDate),
      status: selectedStatus.key === "total"
        ? statusFilterString
        : selectedStatus.key,
      clientOrgIdsForFilters: undefined,
      isForTeam: isForTeamAnalytics,
      delayCategory: chartBarApiFilters.delayCategory,
      priority: chartBarApiFilters.priority,
      includeManagerTasks: includeManagerTasksForPageApi,
      ...(chartBarApiFilters.clientOrgId === "no_client"
        ? { isNotClientTasks: true }
        : chartBarApiFilters.clientOrgId
          ? { clientOrgId: chartBarApiFilters.clientOrgId }
          : {}),
    },
    shouldCallApi
  );

  useEffect(() => {
    if (isActionItemCountSuccess && actionItemCountData) {
      setTotalRecord(actionItemCountData?.count || 0);
    }
    if (isActionItemCountError) {
      showSnackbar("error", actionItemCountError?.message);
    }
  }, [isActionItemCountSuccess, isActionItemCountError, actionItemCountData, actionItemCountError,]);

  const {
    isLoading: isMyActionsLoading,
    isFetching: isMyActionsFetching,
    isSuccess: isMyActionsDataSuccess,
    isError: isMyActionsDataError,
    error: myActionsDataError,
    data: myActionsData,
  } = ActionItemService.useGetPageActionItems(
    {
      employeePorId: chartBarApiFilters.assigneePorIds?.[0] ?? employeePorId ?? loggedInProfileOrgId,
      offset: pageNumber,
      limit: pageSize,
      startTime: formatDateOnly(analyticsDateRange?.startDate),
      endTime: formatDateOnly(analyticsDateRange?.endDate),
      status:
        selectedStatus.key === "total"
          ? statusFilterString
          : selectedStatus.key,
      sortOrder: sort.sort_order,
      sortBy: sort.sort_field,
      clientOrgIdsForFilters: undefined,
      isForTeam: isForTeamAnalytics,
      delayCategory: chartBarApiFilters.delayCategory,
      priority: chartBarApiFilters.priority,
      includeManagerTasks: includeManagerTasksForPageApi,
      ...(chartBarApiFilters.clientOrgId === "no_client"
        ? { isNotClientTasks: true }
        : chartBarApiFilters.clientOrgId
          ? { clientOrgId: chartBarApiFilters.clientOrgId }
          : {}),
    },
    shouldCallApi
  );

  const {
    isSuccess,
    isLoading: isActiveUsersLoading,
    isFetching: isActiveUsersFetching,
    isError,
    error,
    data: activeUsersData,
  } = UserInteractionsService.useGetActiveUsers({}, true);

  const {
    isLoading: isSpacesLoading,
    isSuccess: isSpacesSuccess,
    isError: isSpacesError,
    error: spacesError,
    data: spacesData,
  } = ActionItemService.useGetSpaces(true);

  const {
    isLoading: isDashboardLoading,
    isFetching: isDashboardFetching,
    isSuccess: isDashboardSuccess,
    isError: isDashboardError,
    error: dashboardError,
    data: dashboardData,
  } = ActionItemService.useGetActionItemsDashboard(
    {
      startTime: formatDateOnly(analyticsDateRange?.startDate),
      endTime: formatDateOnly(analyticsDateRange?.endDate),
      employeePorId: employeePorId ?? loggedInProfileOrgId,
      dashboardView: groupBy,
      status:
        selectedStatus.key === "total"
          ? statusFilterString
          : selectedStatus.key,
      isForTeam: isForTeamAnalytics,
      includeManagerTasks: includeManagerTasksForDashboardApi,
    },
    shouldCallApi
  );

  const {
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    isError: isAccountsError,
    error: accountsError,
    data: AccountsData,
  } = ClientService.useGetActiveClients(true);

  useEffect(() => {
    // If API is not enabled, stop the spinner immediately
    if (!shouldCallApi) {
      setShowSpinner(false);
      return;
    }
    setShowSpinner(isMyActionsLoading || isMyActionsFetching);

    // Only update data when not loading/fetching to prevent showing stale cached data
    if (isMyActionsDataSuccess && !isMyActionsLoading && !isMyActionsFetching) {
      const myActionItemList = myActionsData?.data?.data;
      setActionItems(myActionItemList);
    }
    if (isMyActionsDataError) {
      showSnackbar("error", myActionsDataError);
    }
  }, [
    isMyActionsLoading,
    isMyActionsFetching,
    isMyActionsDataSuccess,
    isMyActionsDataError,
    myActionsData,
    shouldCallApi,
  ]);

  useEffect(() => {
    if (isSuccess) {
      const activeUsers = activeUsersData?.data?.data || [];
      setActiveUsers(activeUsers);
    }
    if (isError) {
      showSnackbar("error", `Error fetching active users: ${error}`);
    }
  }, [
    isError,
    error,
    activeUsersData,
    isSuccess,
    isActiveUsersLoading,
    isActiveUsersFetching,
  ]);

  const {
    isLoading: isGroupbyLoading,
    isFetching: isGroupbyFetching,
    isSuccess: isGroupbySuccess,
    isError: isGroupbyError,
    error: groupbyEror,
    data: groupbyData,
  } = ActionItemService.useGetGroupByActionItems(
    {
      startTime: formatDateOnly(analyticsDateRange?.startDate),
      endTime: formatDateOnly(analyticsDateRange?.endDate),
      employeePorId: employeePorId ?? loggedInProfileOrgId,
      groupByActionItem: "status",
      status: statusFilterString,
      isForTeam: isForTeamAnalytics,
      includeManagerTasks: includeManagerTasksForGroupByApi,
    },
    shouldCallApi
  );

  useEffect(() => {
    if (isGroupbySuccess) {
      const groupByList = groupbyData?.data?.statuses;
      setGroupByList(groupByList);
    }
    if (isGroupbyError) {
      showSnackbar("error", groupbyEror?.message);
    }
  }, [
    isGroupbyLoading,
    isGroupbyFetching,
    groupbyData,
    isGroupbyError,
    groupbyEror,
  ]);

  useEffect(() => {
    if (isSpacesSuccess && spacesData) {
      setSpacesList(spacesData?.data || []);
    }
    if (isSpacesError) {
      showSnackbar("error", `Error fetching spaces: ${spacesError}`);
    }
  }, [isSpacesSuccess, isSpacesError, spacesData, isSpacesLoading]);

  useEffect(() => {
    if (AccountsData) {
      const AccountsList = AccountsData?.data || [];
      setClientList(AccountsList);
    } else if (isAccountsError) {
      showSnackbar("error", accountsError?.message || "Failed to load data.");
    }
  }, [
    AccountsData,
    isAccountsError,
    accountsError,
    isAccountsLoading,
    isAccountsFetching,
  ]);

  useEffect(() => {
    if (isDashboardSuccess) {
      const dashBoardList = dashboardData;
      setDashBoardList(dashBoardList);
    } else if (isDashboardError) {
      showSnackbar("error", dashboardError?.message);
    }
  }, [
    dashboardData,
    isDashboardLoading,
    isDashboardFetching,
    isDashboardSuccess,
    isDashboardError,
    dashboardError,
  ]);
  const onPageChange = (value: number) => {
    setActionItems([]); // clear current data
    setPageNumber(value);
  };

  const onFilter = (_value: {
    date: { start_time: string; end_time: string } | undefined;
    status: undefined | string;
  }) => {
    setPageNumber(1);
  };

  const onTypeChange = (value: ActionItemType) => {
    setActionItemType(value);
    setPageNumber(1);
  };
  const chartData: ChartDataItem[] = useMemo(() => {
    if (!dashBoardList) return [];
    const status =
      selectedStatus.key === "total" ? "All" : selectedStatus.label;

    switch (groupBy) {
      case ActionItemGroupByKey.CLIENT:
        return (dashBoardList?.clients || []).map((c: any) => ({
          key: c.clientOrgId ?? "no_client",
          label: capitalizeFirstLetter(
            c.clientName?.trim() === "Not Client Tasks"
              ? "No Account"
              : c.clientName?.trim() || "No Client"
          ),          
          value: c.taskCount ?? 0,
          status,
        }));

        case ActionItemGroupByKey.PRIORITY: {
          const priorityMap = new Map(
            (dashBoardList?.priorities || []).map((p: any) => [
              p.priority ?? "no_priority",
              p.taskCount,
            ])
          );
          const hasNoPriority = priorityMap.has("no_priority");
          const priorityConfigToUse = hasNoPriority
            ? PRIORITY_BAR_CONFIG
            : PRIORITY_BAR_CONFIG.filter(cfg => cfg.value !== "no_priority");
        
          return priorityConfigToUse.map((cfg) => ({
            key: cfg.value,
            label: cfg.label,
            value: priorityMap.get(cfg.value) ?? 0,
            status,
          }));
        }
        
        case ActionItemGroupByKey.RESOURCES: {
          const resources = dashBoardList?.resources || [];
          const filteredResources = isLoggedInUserSelectedInTeam
            ? resources.filter(
                (r: any) => r.employeePorId !== loggedInProfileOrgId
              )
            : resources;
          return filteredResources.map((r: any) => ({
            key: r.employeePorId,
            label: `${getDisplayName(
              r.employeeName,
              r.employeeEmail
            )}`,
            fullLabel: `${r.employeeName?.trim() || r.employeeEmail}`,
            value: r.taskCount ?? 0,
            status,
          }));
        }

        case ActionItemGroupByKey.DELAY:
          const delayMap = new Map(
            (dashBoardList?.delays || []).map((d: any) => [
              d.delayCategory,
              d.taskCount,
            ])
          );
          return DELAY_CATEGORY_CONFIG.map((cfg) => ({
            key: cfg.key,
            label: cfg.label,
            value: delayMap.get(cfg.key) ?? 0,
            status,
          }));

      default:
        return [];
    }
  }, [
    dashBoardList,
    groupBy,
    selectedTab,
    newUserTeamData?.currentUser,
    loggedInProfileOrgId,
  ]);
  const getStatusCount = (statusKey: string) => {
    if (statusKey === "total") return totalTaskCount;
  
    const statusItem = groupByList.find(
      (item) => item.status === statusKey
    );
  
    return statusItem?.totalCount ?? 0;
  };  

  useEffect(() => {
    if (!selectedStatus?.key) return;
  
    const updatedCount = getStatusCount(selectedStatus.key);
  
    setSelectedStatus((prev) => {
      if (prev.count === updatedCount) return prev; // prevent loops
  
      return {
        ...prev,
        count: updatedCount,
      };
    });
  }, [groupByList, totalTaskCount]); 

  useEffect(() => {
    if (selectedTab) {
      setSelectedStatus({
        key: "total",
        label: "Total",
        count: totalTaskCount,
      });
      setSelectedChartBar(null);
      setGroupBy(ActionItemGroupByKey.PRIORITY);
      setGroupByList([]);
      setDashBoardList(null);
      setStatusType("due");
    }
  }, [selectedTab,]);

  useEffect(()=>{
    if(selectedStatus || newUserTeamData?.currentUser || groupBy) {
      setSelectedChartBar(null);
    }
  },[selectedStatus, newUserTeamData?.currentUser, groupBy])

  const filteredGroupByOptions = useMemo(() => {
    if (selectedTab === "my-analytics") {
      return ACTION_ITEM_GROUP_BY_OPTIONS.filter(
        (option) => option.value !== ActionItemGroupByKey.RESOURCES
      );
    }
  
    return ACTION_ITEM_GROUP_BY_OPTIONS;
  }, [selectedTab]);
  
  return (
    <Box className="analytics-tasks-container">
      <Box className="analytics-tasks-main">
        <Box className="analytics-status">
          <StatusBreakdownCard
            statusCounts={filteredGroupByList}
            onStatusClick={(payload) => {
              setSelectedStatus({
                key: payload.key,
                label: payload.label,
                count: payload.count,
              });
            }}
            selectedStatus={selectedStatus}
            loading={isGroupbyLoading || isGroupbyFetching}
            totalCount={totalTaskCount}
            statusType={statusType}
            statusOptions={
              isSeventhGearUser
                ? [
                    { key: "wont_do", label: "Won't Do", color: "#B53737"},
                    { key: "done", label: "Done", color: "#4CAF50" },
                    { key: "in_progress", label: "In Progress", color: "#2F80ED" },
                    { key: "pr_review", label: "PR Review", color: "#875B14" },
                    { key: "product_testing", label: "Product Testing", color: "#195678" },
                    { key: "to_do", label: "To Do", color: "#F2994A" },
                    { key: "total", label: "Total", color: "#8A8A9E" },
                  ]
                : [
                    { key: "wont_do", label: "Won't Do", color: "#B53737"},
                    { key: "done", label: "Done", color: "#4CAF50" },
                    { key: "in_progress", label: "In Progress", color: "#2F80ED" },
                    { key: "to_do", label: "To Do", color: "#F2994A" },
                    { key: "total", label: "Total", color: "#8A8A9E" },
                  ]
            }
            filteredStatusOptions={
              statusType === "overdue"
                ? (isSeventhGearUser
                    ? [
                        { key: "done", label: "Done", color: "#4CAF50" },
                        { key: "in_progress", label: "In Progress", color: "#2F80ED" },
                        { key: "pr_review", label: "PR Review", color: "#875B14" },
                        { key: "product_testing", label: "Product Testing", color: "#195678" },
                        { key: "to_do", label: "To Do", color: "#F2994A" },
                        { key: "total", label: "Total", color: "#8A8A9E" },
                      ].filter(s => s.key !== "done")
                    : [
                        { key: "done", label: "Done", color: "#4CAF50" },
                        { key: "in_progress", label: "In Progress", color: "#2F80ED" },
                        { key: "to_do", label: "To Do", color: "#F2994A" },
                        { key: "total", label: "Total", color: "#8A8A9E" },
                      ].filter(s => s.key !== "done"))
                : (isSeventhGearUser
                    ? [
                        { key: "wont_do", label: "Won't Do", color: "#B53737"},
                        { key: "done", label: "Done", color: "#4CAF50" },
                        { key: "in_progress", label: "In Progress", color: "#2F80ED" },
                        { key: "pr_review", label: "PR Review", color: "#875B14" },
                        { key: "product_testing", label: "Product Testing", color: "#195678" },
                        { key: "to_do", label: "To Do", color: "#F2994A" },
                        { key: "total", label: "Total", color: "#8A8A9E" },
                      ]
                    : [
                        { key: "wont_do", label: "Won't Do", color: "#B53737"},
                        { key: "done", label: "Done", color: "#4CAF50" },
                        { key: "in_progress", label: "In Progress", color: "#2F80ED" },
                        { key: "to_do", label: "To Do", color: "#F2994A" },
                        { key: "total", label: "Total", color: "#8A8A9E" },
                      ])
            }
            statusLabels={{
              statusText: "Status",
              breakdownText: "Tasks Breakdown.",
              dueText: "Due",
              overdueText: "Overdue"
            }}
            onStatusChange={(value) => {
              setStatusType(value);
              setSelectedStatus({
                key: "total",
                label: "Total",
                count: totalTaskCount,
              });
              setSelectedChartBar(null);
              if (value === "overdue") {
                const range = getLastTwoYearsToYesterdayRange();
            
                setAnalyticsDateRange({
                  preset: "custom",
                  startDate: range.start_time,
                  endDate: range.end_time,
                  overdue: true,
                });
              } else {
                // back to initial default
                setAnalyticsDateRange({
                  preset: "currentWeek",
                  startDate: "",
                  endDate: "",
                  overdue: false,
                });
              }
            }}
          />
        </Box>

        <Box className="analytics-tasks-bar-card">
          <AnalyticsBarCard
            title={selectedStatus.label}
            totalCount={selectedStatus.count}
            groupBy={groupBy}
            groupByOptions={filteredGroupByOptions}
            onGroupByChange={setGroupBy}
            chartData={chartData}
            loading={isDashboardLoading || isDashboardFetching}
            onBarClick={(item) =>
              setSelectedChartBar({
                key: item.key ?? item.label,
                label: item.label,
                value: item.value,
                groupBy,
              })
            }
            selectedChartBar={selectedChartBar}
            isSeventhGearUser={isSeventhGearUser}
            hoverText="Tasks"
            barChartHeight={isSeventhGearUser ? 300 : 230}
            showPagination={false}
          />
        </Box>
      </Box>
      <Box className="analytics-tasks-table">
        <Box className="table-header">
          <Box className="task-table-title">
            <h1 className="table-title-text">Tasks List</h1>
            <Box className="table-filters">
              <FilterPill
                label={"Status"}
                value={
                  selectedStatus.key === "total"
                    ? isSeventhGearUser ? ["to_do", "in_progress", "done", "pr_review", "product_testing", "wont_do"] : ["to_do", "in_progress", "done", "wont_do"]
                    : [selectedStatus.key]
                }
                options={filteredStatusOptions}
                displayValue={selectedStatus.key === "total" ? "All" : undefined}
                displayAll = {true}
                showClearIcon={selectedStatus.key !== "total"}
                onChange={() => {
                  // reset to default when clear is clicked
                  setSelectedStatus({
                    key: "total",
                    label: "Total",
                    count: totalTaskCount,
                  });
                }}
                readOnly={true}
              />
              {chartBarPills.map(({ key, value }) => {
                const pillColumns = ["account", "assignee", "priority", "delay"];
                if (!pillColumns.includes(key)) return null;

                let options: Options[] = [];
                let displayValue = "";
                let avatar: string | undefined;
                let email: string | undefined;

                switch (key) {
                  case "assignee":
                    options = getUniqueAssignees({
                      usersList: activeUsers,
                    }).map((o) => ({
                      ...o,
                      type: "assignee",
                      email: o.email,
                    }));
                    const assignee = options.find((o) => o.value === value);
                    displayValue = assignee?.label || "";
                    avatar = assignee?.avatar;
                    email = assignee?.email;
                    break;
                  case "account":
                    options = [
                      { label: "No Account", value: "no_client", type: "account" },
                      ...getUniqueAccounts({ accountList: clientList }).map((o) => ({
                        ...o,
                        type: "account",
                      })),
                    ];
                    displayValue =
                      options.find((o) => o.value === value)?.label || "";
                    break;

                  case "priority":
                    options = PRIORITY_OPTION;
                    displayValue =
                      options.find((o) => o.value === value)?.label || "";
                    break;

                  case "delay": {
                    const delayConfig = DELAY_CATEGORY_CONFIG.find(
                      (d) => d.key === value
                    );
                  
                    displayValue = delayConfig?.label || value;
                    options = DELAY_CATEGORY_CONFIG.map((d) => ({
                      label: d.label,
                      value: d.key,
                      type: "delay",
                    }));
                    break;
                  }
                }

                return (
                  <FilterPill
                    key={key}
                    label={capitalizeFirstLetter(key)}
                    options={options}
                    value={[value]}
                    displayValue={displayValue}
                    avatar={avatar}
                    email={email}
                    readOnly
                    showClearIcon
                    onChange={() => setSelectedChartBar(null)}
                    groupBy={groupBy}
                  />
                );
              })}
            </Box>
          </Box>
          <Box className="table-actions">
            <ColumnFilter
              currentValue={columnVisibility ?? {}} // Pass the visibility state (Record<string, boolean>)
              onApply={(val) => setColumnVisibility?.(val as ColumnVisibility)}
              options={[
                { value: "description", label: "Title", defaultValue: true },
                {
                  value: "assignee",
                  label: "Solo Player",
                  defaultValue: true,
                },
                { value: "account", label: "Account" },
                { value: "dueDate", label: "Due Date", defaultValue: true },
                { value: "priority", label: "Priority" },
                { value: "status", label: "Status" },
                { value: "coach", label: "Solo Coach" },
                { value: "momlink", label: "MoM Link" }, // Added MoM Link option
                { value: "sponsor", label: "Exec Sponsor" }, // Added Sponsor option
                { value: "tags", label: "Tags" },
                { value: "subscribers", label: "Subscribers" },
                { value: "createdAt", label: "Created At" },
                { value: "actions", label: "Actions" },
              ]}
              type="toggle"
              showSections={{
                search: true,
                sort: false,
              }}
            />
          </Box>
        </Box>
        <Box className="table-content">
          <ActionItems
            type={
              selectedTab === "my-analytics"
                ? "MY_ACTION_ITEMS"
                : "TEAM_ACTION_ITEMS"
            }
            isLoading={isMyActionsLoading || isMyActionsFetching}
            totalRecord={totalRecord}
            actionItems={actionItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onFilter={onFilter}
            actionItemType={actionItemType}
            onTypeChange={onTypeChange}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            sort={sort}
            onSortChange={(columnKey, direction) =>
              setSort({
                sort_field: columnKey,
                sort_order: direction,
              })
            }
            showSpinner={showSpinner}
            setShowSpinner={setShowSpinner}
            enableRowSelection={false}
            tableMaxHeight={"400px"}
            tableMinHeight = {"auto"}
            isReadOnly={true}
            activeUsers={activeUsers}
            spacesList={spacesList}
            columnVisibility={columnVisibility}
            isCountLoading={isActionItemCountLoading || isActionItemCountFetching}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AnalyticsTasks;
